import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  groupHoldersByPerson,
  keptAfterRemoving,
  scopeAlertCount,
} from "@/api/roles-permissions/roleHolders";
import type { ThisRole } from "@/api/roles-permissions/roleHolders";
import type { RoleHolder, RoleHolderRowView } from "@/api/roles-permissions/roleHolders.types";
import {
  fetchRoleHolders,
  PermissionsQueryKeys,
} from "@/api/roles-permissions/roles-permissions.queries";

/**
 * The per-person reads the person record and the Set Scope walk-through make; the role write and
 * the membership writes invalidate them. The holders key is the one the role reads already use,
 * so one invalidation reaches both.
 */
export const RoleHoldersQueryKeys = {
  userScope: "userScope",
  userRoles: "userHeldRoles",
} as const;

/**
 * The holders endpoint pages over (person, role copy) rows. The Users tab needs every holder at
 * once — a person's entities are spread across rows, and the scope Alert counts the whole role —
 * so the rows are read in full here and the table pages them itself.
 */
const HOLDERS_PAGE_SIZE = 500;
const MAX_HOLDER_PAGES = 10;

const fetchAllHolderRows = async (
  roleId: string,
  signal: AbortSignal,
): Promise<{ rows: RoleHolder[]; complete: boolean }> => {
  const first = await fetchRoleHolders(roleId, { page: 1, pageSize: HOLDERS_PAGE_SIZE }, signal);
  const rows = [...first.data];
  const totalPages = first.totalPages ?? 1;
  const lastPage = Math.min(totalPages, MAX_HOLDER_PAGES);

  for (let page = 2; page <= lastPage; page += 1) {
    const next = await fetchRoleHolders(roleId, { page, pageSize: HOLDERS_PAGE_SIZE }, signal);
    rows.push(...next.data);
  }

  return { rows, complete: totalPages <= MAX_HOLDER_PAGES };
};

export type RoleHoldersResult = {
  rows: RoleHolderRowView[];
  /** How many people hold the role, or `null` until every holder row is in. */
  peopleCount: number | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** How many holders have a copy whose scope the holders row says is not set. */
  needsScopeCount: number;
  /**
   * True when the count is a floor rather than the role's total: the list was truncated, or the
   * read could not judge somebody's scope. The Alert then says "at least".
   */
  needsScopeIsPartial: boolean;
};

/**
 * Who holds a role. Status, other roles and whether scope is set all come on the holders row
 * (SERV-3520, SERV-3573), so the tab makes one read for the whole role rather than two per holder
 * (SERV-3577, UI7).
 */
export function useRoleHolders(role: ThisRole | null): RoleHoldersResult {
  const roleId = role?.id ?? "";

  const holders = useQuery({
    queryKey: [PermissionsQueryKeys.getRoleHolders, roleId, "all"],
    enabled: roleId !== "",
    queryFn: ({ signal }) => fetchAllHolderRows(roleId, signal),
  });

  const rows = useMemo<RoleHolderRowView[]>(
    () =>
      (holders.data ? groupHoldersByPerson(holders.data.rows) : []).map((person) => ({
        ...person,
        keptAfterRemoval: role ? keptAfterRemoving(person, role) : null,
      })),
    [holders.data, role],
  );

  // Ended rows are dropped in `groupHoldersByPerson`, so every row here is a current holding.
  const { needsScopeCount, needsScopeIsPartial } = scopeAlertCount({
    rows,
    everyHolderRead: holders.data?.complete === true,
  });

  return {
    rows,
    // How many people hold the role, or `null` until they are all in. The record's own
    // `holderCount` cannot answer this: it counts (person, role copy) rows, so a person who
    // holds the role in two entities counts twice and the tab would disagree with its table.
    peopleCount: holders.data?.complete === true ? rows.length : null,
    isLoading: holders.isPending && roleId !== "",
    isError: holders.isError,
    refetch: () => void holders.refetch(),
    needsScopeCount,
    needsScopeIsPartial,
  };
}
